/**
 * Partner Signup API functions
 * API calls for partner referral validation, signup, and email availability
 *
 * FR-001: Validate partner referral code
 * FR-004: Create user account via partner signup
 * FR-007: Check email availability (debounced by caller)
 */

import { apiClient } from '@/core/api/client';
import type {
  PartnerCompanyInfo,
  PartnerSignupRequest,
  PartnerSignupResponse,
  EmailAvailabilityResponse,
  SelectionStatusResponse,
  AdvisorSearchResponse,
  SelectAdvisorResponse,
  SkipAdvisorResponse,
} from '../types';

/**
 * Validate a partner company referral code
 * GET /partner-referral/:code (public, rate limited)
 * FR-001, FR-002, FR-003: Case-insensitive code lookup
 */
export const validateReferralCode = async (
  code: string,
): Promise<PartnerCompanyInfo> => {
  const response = await apiClient.get<PartnerCompanyInfo>(
    `/partner-referral/${encodeURIComponent(code)}`,
  );
  return response.data;
};

/**
 * Create a new user account via partner referral
 * POST /partner-signup (public, rate limited)
 * FR-004, FR-005, FR-006, FR-008
 */
export const partnerSignup = async (
  data: PartnerSignupRequest,
): Promise<PartnerSignupResponse> => {
  const response = await apiClient.post<PartnerSignupResponse>(
    '/partner-signup',
    data,
  );
  return response.data;
};

/**
 * Check if an email address is already registered
 * GET /users/check-email/:email (public)
 * FR-007: Real-time email availability check
 */
export const checkEmailAvailability = async (
  email: string,
): Promise<EmailAvailabilityResponse> => {
  const response = await apiClient.get<EmailAvailabilityResponse>(
    `/users/check-email/${encodeURIComponent(email)}`,
  );
  return response.data;
};

/**
 * Get user's advisor selection status
 * GET /advisor-selection/status (authenticated)
 * FR-036: Check if user needs to complete advisor selection
 * Used post-login to determine if user should be redirected to /select-advisor
 */
export const getSelectionStatus = async (): Promise<SelectionStatusResponse> => {
  const response = await apiClient.get<SelectionStatusResponse>(
    '/advisor-selection/status',
  );
  return response.data;
};

/**
 * Search for financial advisors in the user's partner organization
 * GET /advisor-selection/search (authenticated)
 * FR-016, FR-017, FR-018, FR-019, FR-020
 *
 * @param query - Search query (minimum 2 characters)
 * @param page - Page number (1-indexed)
 * @param limit - Results per page (default 10)
 */
export const searchAdvisors = async (
  query: string,
  page: number = 1,
  limit: number = 10,
): Promise<AdvisorSearchResponse> => {
  const response = await apiClient.get<AdvisorSearchResponse>(
    '/advisor-selection/search',
    {
      params: { query, page, limit },
    },
  );
  return response.data;
};

/**
 * Select a financial advisor to create a client-advisor relationship
 * PUT /advisor-selection/select (authenticated)
 * FR-021, FR-022, FR-023, FR-024, FR-025, FR-028, FR-029, FR-030, FR-032, FR-033
 *
 * @param advisorId - The UUID of the advisor to select
 */
export const selectAdvisor = async (
  advisorId: string,
): Promise<SelectAdvisorResponse> => {
  const response = await apiClient.put<SelectAdvisorResponse>(
    '/advisor-selection/select',
    { advisorId },
  );
  return response.data;
};

/**
 * Skip advisor selection for now
 * PUT /advisor-selection/skip (authenticated)
 * FR-026, FR-027
 *
 * User can skip multiple times and will be prompted again on subsequent logins
 */
export const skipAdvisorSelection = async (): Promise<SkipAdvisorResponse> => {
  const response = await apiClient.put<SkipAdvisorResponse>(
    '/advisor-selection/skip',
  );
  return response.data;
};
